import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../../lib/firebase';
import { getTicketLink, recordGuestTicketPurchase, type TicketLink, type TicketTier } from '../../services/ticketLinks';
import { ticketPurchasedEmail } from '../../services/email';
import { getAllListings, type Listing } from '../../services/listings';
import { getSiteSettings, type SocialLinks } from '../../services/settings';
import { doc, getDoc } from 'firebase/firestore';

// Platform → icon + href builder, mirroring the main site Footer.
const SOCIAL_META: { key: keyof SocialLinks; label: string; icon: string; href: (v: string) => string }[] = [
  { key: 'instagram', label: 'Instagram', icon: 'ri-instagram-line', href: (v) => v },
  { key: 'twitter', label: 'X (Twitter)', icon: 'ri-twitter-x-line', href: (v) => v },
  { key: 'facebook', label: 'Facebook', icon: 'ri-facebook-circle-line', href: (v) => v },
  { key: 'tiktok', label: 'TikTok', icon: 'ri-tiktok-line', href: (v) => v },
  { key: 'youtube', label: 'YouTube', icon: 'ri-youtube-line', href: (v) => v },
  { key: 'linkedin', label: 'LinkedIn', icon: 'ri-linkedin-box-line', href: (v) => v },
  { key: 'whatsapp', label: 'WhatsApp', icon: 'ri-whatsapp-line', href: (v) => (/^https?:|^mailto:|^tel:/.test(v) ? v : `https://wa.me/${v.replace(/[^0-9]/g, '')}`) },
];

const naira = (n: number) => `₦${Number(n || 0).toLocaleString('en-NG')}`;

// Load Paystack inline lazily so a guest never hits a missing global.
const ensurePaystack = (): Promise<any> =>
  new Promise((resolve, reject) => {
    const w = window as any;
    if (w.PaystackPop) return resolve(w.PaystackPop);
    const existing = document.querySelector('script[src="https://js.paystack.co/v1/inline.js"]') as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve((window as any).PaystackPop));
      existing.addEventListener('error', () => reject(new Error('Payment library failed to load')));
      if ((window as any).PaystackPop) resolve((window as any).PaystackPop);
      return;
    }
    const s = document.createElement('script');
    s.src = 'https://js.paystack.co/v1/inline.js';
    s.async = true;
    s.onload = () => resolve((window as any).PaystackPop);
    s.onerror = () => reject(new Error('Payment library failed to load'));
    document.body.appendChild(s);
  });

export default function TicketPurchasePage() {
  const { linkId } = useParams();
  const [link, setLink] = useState<TicketLink | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sellerName, setSellerName] = useState('');

  const [tierIdx, setTierIdx] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [buyerName, setBuyerName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [buyerPhone, setBuyerPhone] = useState('');

  const [paying, setPaying] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successCodes, setSuccessCodes] = useState<string[] | null>(null);

  // A few random marketplace items to cross-sell at the bottom of the page.
  const [marketItems, setMarketItems] = useState<Listing[]>([]);
  const [socials, setSocials] = useState<{ label: string; href: string; icon: string }[]>([]);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        if (!linkId) { setLoadError('Missing ticket link.'); return; }
        const data = await getTicketLink(linkId);
        if (!active) return;
        if (!data) { setLoadError('This ticket link was not found.'); return; }
        setLink(data);
      } catch {
        if (active) setLoadError('Could not load this ticket. Please try again.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [linkId]);

  // Load a handful of random marketplace items (not tickets) to cross-sell.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const all = await getAllListings();
        if (!active) return;
        const items = all.filter(l => l.type !== 'ticket' && !l.soldOut && (l.image || (l.images && l.images[0])));
        // Shuffle then take 6.
        for (let i = items.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [items[i], items[j]] = [items[j], items[i]];
        }
        setMarketItems(items.slice(0, 6));
      } catch { /* ignore */ }
      try {
        const s = await getSiteSettings();
        if (!active) return;
        const configured = SOCIAL_META
          .filter((m) => (s.social?.[m.key] || '').trim())
          .map((m) => ({ label: m.label, href: m.href((s.social![m.key] as string).trim()), icon: m.icon }));
        // Always show Campket handles to buyers: fall back to defaults when the
        // admin hasn't configured social links yet.
        const DEFAULTS = [
          { label: 'WhatsApp', href: 'https://wa.me/2349074202400', icon: 'ri-whatsapp-line' },
          { label: 'More links', href: 'https://linktr.ee/synccoreinnovations', icon: 'ri-links-line' },
        ];
        setSocials(configured.length ? configured : DEFAULTS);
      } catch {
        if (active) setSocials([
          { label: 'WhatsApp', href: 'https://wa.me/2349074202400', icon: 'ri-whatsapp-line' },
          { label: 'More links', href: 'https://linktr.ee/synccoreinnovations', icon: 'ri-links-line' },
        ]);
      }
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      const sid = link?.sellerId;
      if (!sid) return;
      try {
        const snap = await getDoc(doc(db, 'users', sid));
        if (!active) return;
        const u = snap.exists() ? (snap.data() as any) : null;
        setSellerName(u?.fullName || u?.displayName || u?.name || '');
      } catch { /* ignore */ }
    })();
    return () => { active = false; };
  }, [link?.sellerId]);

  const tiers: TicketTier[] = useMemo(() => {
    if (link?.tiers && link.tiers.length) return link.tiers;
    if (link) return [{ name: 'General Admission', price: link.price }];
    return [];
  }, [link]);

  const tier = tiers[tierIdx] || tiers[0];
  const unitPrice = Number(tier?.price || 0);
  const total = unitPrice * quantity;
  const image = link?.images?.[0];

  const onlineUrl = (link?.onlineUrl || '').trim();
  const isOnline = !!onlineUrl;
  // Build a Google Maps embed query for physical events from venue + place.
  const locationParts = link ? [link.venue, (link as any).state, link.university].filter(Boolean) : [];
  const locationLabel = locationParts.join(', ');
  const mapQuery = encodeURIComponent(locationLabel);

  const tierRemaining = (t: TicketTier) =>
    typeof t.quantity === 'number' ? Math.max(0, t.quantity - Number(t.soldCount || 0)) : Infinity;
  const tierSoldOut = tier ? tierRemaining(tier) <= 0 : false;
  const maxForTier = tier ? tierRemaining(tier) : Infinity;

  const isValidEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());

  const handlePay = async () => {
    setFormError(null);
    if (!link || !linkId) return;
    if (buyerName.trim().length < 2) { setFormError('Please enter your full name.'); return; }
    if (!isValidEmail(buyerEmail)) { setFormError('Enter a valid email — your ticket is sent there.'); return; }
    const phoneDigits = buyerPhone.replace(/\D/g, '');
    if (phoneDigits.length < 10 || phoneDigits.length > 15) { setFormError('Enter a valid phone / WhatsApp number.'); return; }
    if (quantity < 1) { setFormError('Choose at least 1 ticket.'); return; }
    if (tierSoldOut) { setFormError('This ticket type is sold out.'); return; }
    if (quantity > maxForTier) { setFormError(`Only ${maxForTier} left for this ticket type.`); return; }

    const key = import.meta.env.VITE_PAYSTACK_PUBLIC_KEY as string | undefined;
    if (!key) { setFormError('Payments are not configured. Please contact the organizer.'); return; }

    setPaying(true);
    try {
      const PaystackPop = await ensurePaystack();
      const reference = `TICKET-${linkId}-${Date.now()}`;
      const handler = PaystackPop.setup({
        key,
        email: buyerEmail.trim(),
        amount: Math.round(total * 100),
        currency: 'NGN',
        ref: reference,
        metadata: {
          custom_fields: [
            { display_name: 'Event', variable_name: 'event', value: link.title },
            { display_name: 'Buyer', variable_name: 'buyer', value: buyerName.trim() },
            { display_name: 'Tier', variable_name: 'tier', value: tier?.name || '' },
            { display_name: 'Quantity', variable_name: 'quantity', value: String(quantity) },
            { display_name: 'Phone', variable_name: 'phone', value: buyerPhone.trim() },
          ],
        },
        callback: (response: any) => {
          (async () => {
            try {
              const ref = response?.reference || reference;
              // Verify the charge server-side before issuing tickets.
              const vResp = await fetch(`/api/paystack-verify?reference=${encodeURIComponent(ref)}`);
              const vData = await vResp.json().catch(() => null);
              const tx = vData?.data;
              if (!vResp.ok || tx?.status !== 'success') {
                setFormError(`We could not confirm your payment. If you were charged, contact the organizer with ref: ${ref}`);
                setPaying(false);
                return;
              }
              const res = await recordGuestTicketPurchase({
                linkId,
                tierName: tier?.name,
                unitPrice,
                quantity,
                buyerName: buyerName.trim(),
                buyerEmail: buyerEmail.trim(),
                buyerPhone: buyerPhone.trim() || undefined,
                paymentReference: ref,
              });
              // Fire-and-forget the e-ticket + seller notification emails.
              ticketPurchasedEmail({
                buyerEmail: buyerEmail.trim(),
                buyerName: buyerName.trim(),
                sellerEmail: link.sellerEmail,
                sellerName: sellerName || link.sellerName,
                eventTitle: link.title,
                eventDate: link.eventDate,
                venue: link.venue,
                tierName: tier?.name,
                quantity,
                unitPrice,
                total: res.total,
                sellerAmount: res.sellerAmount,
                codes: res.codes,
              }).catch(() => {});
              setSuccessCodes(res.codes);
            } catch (e: any) {
              setFormError(String(e?.message || 'Something went wrong issuing your ticket.'));
            } finally {
              setPaying(false);
            }
          })();
        },
        onClose: () => { setPaying(false); },
      });
      handler.openIframe();
    } catch (e: any) {
      setFormError(String(e?.message || 'Could not start payment.'));
      setPaying(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      {/* Minimal branded bar */}
      <header className="sticky top-0 z-30 border-b border-gray-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-600 text-white"><i className="ri-graduation-cap-fill"></i></span>
            <span className="text-lg font-bold text-gray-900">Campus Market <span className="font-normal text-gray-400">Tickets</span></span>
          </Link>
          <span className="hidden items-center gap-1 text-xs font-medium text-gray-500 sm:flex"><i className="ri-lock-2-line text-green-600"></i> Secure checkout</span>
        </div>
      </header>

      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-6">
        {loading ? (
          <div className="rounded-2xl bg-white p-10 text-center shadow-sm ring-1 ring-gray-100">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-b-2 border-teal-600"></div>
            <p className="mt-3 text-gray-500">Loading ticket…</p>
          </div>
        ) : loadError ? (
          <div className="rounded-2xl bg-white p-10 text-center shadow-sm ring-1 ring-gray-100">
            <i className="ri-ticket-2-line text-5xl text-gray-300"></i>
            <p className="mt-3 font-medium text-gray-700">{loadError}</p>
            <Link to="/" className="mt-4 inline-block text-teal-600 hover:text-teal-700">Back to Campus Market</Link>
          </div>
        ) : link ? (
          <>
            {/* Event hero banner */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900 to-teal-800 shadow-sm">
              <div className="h-56 w-full sm:h-72 md:h-80">
                {image ? (
                  <img src={image} alt={link.title} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-white/30"><i className="ri-calendar-event-line text-7xl"></i></div>
                )}
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent"></div>
              <div className="absolute bottom-0 p-5 text-white sm:p-7">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-teal-600 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide"><i className="ri-ticket-2-fill"></i> Event Ticket</span>
                  {isOnline && <span className="inline-flex items-center gap-1 rounded-full bg-indigo-600 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide"><i className="ri-global-line"></i> Online event</span>}
                </div>
                <h1 className="mt-2 text-2xl font-bold leading-tight sm:text-4xl">{link.title}</h1>
                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-white/90">
                  {link.eventDate && <span><i className="ri-calendar-line mr-1"></i>{link.eventDate}</span>}
                  {isOnline ? <span><i className="ri-global-line mr-1"></i>Online</span> : (link.venue || (link as any).state || link.university) && <span><i className="ri-map-pin-line mr-1"></i>{locationLabel}</span>}
                  {(sellerName || link.sellerName) && <span><i className="ri-store-2-line mr-1"></i>{sellerName || link.sellerName}</span>}
                </div>
              </div>
            </div>

            <div className="mt-6 grid gap-6 lg:grid-cols-5">
              {/* Buy box — first on mobile, sticky right on desktop */}
              <div className="order-1 lg:order-2 lg:col-span-2">
                <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-100 lg:sticky lg:top-20">
                  <h2 className="text-lg font-bold text-gray-900">Get tickets</h2>
                  <p className="mb-4 text-xs text-gray-500">Select a ticket type and quantity.</p>

                  <div className="space-y-2">
                    {tiers.map((t, i) => {
                      const remaining = tierRemaining(t);
                      const soldOut = remaining <= 0;
                      const active = i === tierIdx;
                      return (
                        <button
                          key={`${t.name}-${i}`}
                          disabled={soldOut}
                          onClick={() => { setTierIdx(i); setQuantity(1); }}
                          className={`flex w-full items-center justify-between rounded-xl border p-3 text-left transition-colors ${
                            soldOut ? 'cursor-not-allowed border-gray-100 bg-gray-50 opacity-60'
                            : active ? 'border-teal-500 bg-teal-50 ring-1 ring-teal-500'
                            : 'border-gray-200 hover:border-teal-300'
                          }`}
                        >
                          <div className="min-w-0">
                            <div className="font-semibold text-gray-900">{t.name}</div>
                            {t.perks && (
                              <div className="mt-1 flex items-start gap-1 text-xs font-medium text-teal-700">
                                <i className="ri-checkbox-circle-line mt-0.5 flex-shrink-0"></i>
                                <span className="whitespace-pre-wrap">{t.perks}</span>
                              </div>
                            )}
                            {typeof t.quantity === 'number' && (
                              <div className="text-[11px] text-gray-400">{soldOut ? 'Sold out' : `${remaining} left`}</div>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-teal-700">{naira(t.price)}</span>
                            <span className={`flex h-5 w-5 items-center justify-center rounded-full border ${active ? 'border-teal-600 bg-teal-600 text-white' : 'border-gray-300'}`}>
                              {active && <i className="ri-check-line text-xs"></i>}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-900">Quantity</span>
                    <div className="flex items-center gap-3">
                      <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 text-lg text-gray-700 hover:bg-gray-50">−</button>
                      <span className="w-6 text-center font-semibold">{quantity}</span>
                      <button onClick={() => setQuantity(q => Math.min(maxForTier, q + 1))} className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 text-lg text-gray-700 hover:bg-gray-50">+</button>
                    </div>
                  </div>

                  <div className="mt-4 space-y-2.5 border-t border-gray-100 pt-4">
                    <p className="text-xs text-gray-500">All fields are required.</p>
                    <input value={buyerName} onChange={(e) => setBuyerName(e.target.value)} placeholder="Your full name *" className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-teal-400 focus:ring-2 focus:ring-teal-100" />
                    <input value={buyerEmail} onChange={(e) => setBuyerEmail(e.target.value)} type="email" placeholder="Email — ticket is sent here *" className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-teal-400 focus:ring-2 focus:ring-teal-100" />
                    <input value={buyerPhone} onChange={(e) => setBuyerPhone(e.target.value)} inputMode="tel" placeholder="Phone / WhatsApp number *" className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-teal-400 focus:ring-2 focus:ring-teal-100" />
                  </div>

                  {formError && <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{formError}</p>}

                  <div className="mt-4 flex items-center justify-between">
                    <div>
                      <div className="text-xs text-gray-500">Total</div>
                      <div className="text-2xl font-bold text-gray-900">{naira(total)}</div>
                    </div>
                    <div className="text-right text-xs text-gray-400">{quantity} × {naira(unitPrice)}</div>
                  </div>

                  <button
                    onClick={handlePay}
                    disabled={paying || tierSoldOut}
                    className="mt-3 w-full rounded-xl bg-teal-600 px-6 py-3.5 text-base font-semibold text-white shadow-lg shadow-teal-600/20 transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {paying ? 'Processing…' : tierSoldOut ? 'Sold out' : `Pay ${naira(total)}`}
                  </button>

                  <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] text-gray-400">
                    <span><i className="ri-user-smile-line mr-1"></i>No account needed</span>
                    <span><i className="ri-shield-check-line mr-1 text-green-600"></i>Secure by Paystack</span>
                    <span><i className="ri-mail-send-line mr-1"></i>Instant email</span>
                  </div>
                </div>
              </div>

              {/* Event details — second on mobile, left on desktop */}
              <div className="order-2 space-y-5 lg:order-1 lg:col-span-3">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="flex items-start gap-3 rounded-2xl bg-white p-4 ring-1 ring-gray-100">
                    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-teal-50 text-teal-600"><i className="ri-calendar-event-line text-xl"></i></span>
                    <div><div className="text-xs font-semibold uppercase tracking-wide text-gray-400">Date &amp; time</div><div className="font-medium text-gray-900">{link.eventDate || 'See details'}</div></div>
                  </div>
                  <div className="flex items-start gap-3 rounded-2xl bg-white p-4 ring-1 ring-gray-100">
                    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-teal-50 text-teal-600"><i className="ri-map-pin-2-line text-xl"></i></span>
                    <div><div className="text-xs font-semibold uppercase tracking-wide text-gray-400">Location</div><div className="font-medium text-gray-900">{isOnline ? 'Online event' : (locationLabel || 'See details')}</div></div>
                  </div>
                </div>

                {isOnline ? (
                  <div className="rounded-2xl bg-white p-5 ring-1 ring-gray-100">
                    <h3 className="mb-2 flex items-center gap-2 text-base font-bold text-gray-900"><i className="ri-global-line text-indigo-600"></i> Online event</h3>
                    <p className="text-sm text-gray-600">This is an online event. Your access link will be shown after purchase and emailed to you.</p>
                  </div>
                ) : locationLabel ? (
                  <div className="overflow-hidden rounded-2xl bg-white p-5 ring-1 ring-gray-100">
                    <h3 className="mb-3 flex items-center gap-2 text-base font-bold text-gray-900"><i className="ri-map-pin-2-line text-teal-600"></i> Location</h3>
                    <p className="mb-3 text-sm text-gray-600">{locationLabel}</p>
                    <div className="aspect-video w-full overflow-hidden rounded-xl ring-1 ring-gray-200">
                      <iframe
                        title="Event location map"
                        src={`https://www.google.com/maps?q=${mapQuery}&output=embed`}
                        className="h-full w-full border-0"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        allowFullScreen
                      ></iframe>
                    </div>
                  </div>
                ) : null}

                {link.description && (
                  <div className="rounded-2xl bg-white p-5 ring-1 ring-gray-100">
                    <h3 className="mb-2 text-base font-bold text-gray-900">About this event</h3>
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-600">{link.description}</p>
                  </div>
                )}

                <div className="flex items-center gap-3 rounded-2xl bg-white p-4 ring-1 ring-gray-100">
                  <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600"><i className="ri-store-2-line text-xl"></i></span>
                  <div><div className="text-xs font-semibold uppercase tracking-wide text-gray-400">Organized by</div><div className="font-medium text-gray-900">{sellerName || link.sellerName || 'Campus Market seller'}</div></div>
                </div>
              </div>
            </div>
          </>
        ) : null}

        {/* Cross-sell: a few random marketplace items */}
        {marketItems.length > 0 && (
          <section className="mt-10">
            <div className="mb-4 flex items-end justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900 sm:text-xl">Browse more on Campus Market</h2>
                <p className="text-sm text-gray-500">Fresh deals from student sellers near you.</p>
              </div>
              <Link to="/marketplace" className="hidden items-center gap-1 text-sm font-semibold text-teal-600 hover:text-teal-700 sm:inline-flex">
                See all <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {marketItems.map((it) => (
                <Link
                  key={it.id}
                  to={`/product/${it.id}`}
                  className="group overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-100 transition hover:shadow-md"
                >
                  <div className="aspect-square w-full overflow-hidden bg-gray-100">
                    <img
                      src={it.image || it.images?.[0]}
                      alt={it.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition group-hover:scale-105"
                    />
                  </div>
                  <div className="p-2.5">
                    <div className="truncate text-xs font-medium text-gray-800">{it.title}</div>
                    <div className="mt-0.5 text-sm font-bold text-teal-700">{naira(it.price)}</div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="mt-5 text-center sm:hidden">
              <Link to="/marketplace" className="inline-flex items-center gap-1.5 rounded-xl border border-teal-200 bg-teal-50 px-5 py-2.5 text-sm font-semibold text-teal-700">
                Browse more on Campus Market <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </section>
        )}
      </main>

      {/* Minimal footer */}
      <footer className="border-t border-gray-200 bg-white py-6 text-center text-xs text-gray-400">
        {socials.length > 0 && (
          <div className="mb-3">
            <div className="mb-2 font-semibold uppercase tracking-wide text-gray-500">Follow us</div>
            <div className="flex items-center justify-center gap-3">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-colors hover:bg-teal-600 hover:text-white"
                >
                  <i className={`${s.icon} text-base`}></i>
                </a>
              ))}
            </div>
          </div>
        )}
        Powered by <Link to="/" className="font-medium text-teal-600 hover:text-teal-700">Campus Market</Link> · Secure payments by Paystack
      </footer>

      {/* Success */}
      {successCodes && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-xl">
            <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <i className="ri-checkbox-circle-fill text-4xl text-green-500"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900">You're in! 🎉</h3>
            <p className="mt-1 text-sm text-gray-500">
              {successCodes.length > 1 ? `${successCodes.length} tickets` : 'Your ticket'} for <strong>{link?.title}</strong>.
              We've emailed {successCodes.length > 1 ? 'them' : 'it'} to <strong>{buyerEmail}</strong>.
            </p>
            <div className="my-4 space-y-2">
              {successCodes.map((c, i) => (
                <div key={c} className="flex items-center gap-3 rounded-xl bg-teal-50 p-3 text-left ring-1 ring-teal-100">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&margin=0&data=${encodeURIComponent(c)}`}
                    alt={`QR code for ticket ${i + 1}`}
                    width={64}
                    height={64}
                    className="h-16 w-16 flex-shrink-0 rounded-lg bg-white p-1 ring-1 ring-teal-100"
                  />
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-teal-700">Ticket {i + 1}</div>
                    <div className="break-all font-mono text-base font-bold tracking-wider text-gray-900">{c}</div>
                    <div className="text-[10px] text-gray-500">Show this QR or the code at entry</div>
                  </div>
                </div>
              ))}
            </div>
            {isOnline ? (
              <div className="mb-4 rounded-xl border border-indigo-200 bg-indigo-50 p-3 text-left">
                <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-indigo-700"><i className="ri-global-line"></i> Online event link</div>
                <a href={onlineUrl} target="_blank" rel="noopener noreferrer" className="block break-all text-sm font-medium text-indigo-700 underline hover:text-indigo-800">{onlineUrl}</a>
                <p className="mt-1 text-[11px] text-indigo-600/80">Also emailed to you. Keep your ticket code(s) handy to join.</p>
              </div>
            ) : (
              <p className="mb-4 text-xs text-gray-500">Show your code(s) at the entrance.</p>
            )}
            <button onClick={() => setSuccessCodes(null)} className="w-full rounded-lg bg-teal-600 py-3 font-medium text-white hover:bg-teal-700">Done</button>
            <Link to="/marketplace" className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-200 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">
              <i className="ri-store-2-line"></i> Visit Campus Marketplace
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
